import { createFileRoute, redirect } from "@tanstack/react-router"
import { z } from "zod"

import { loadTerminalPortfolios } from "@/actions/terminal-file-actions"
import { CompaniesBoardPage } from "@/admin/companies/board-page"
import { loadCompaniesBoard } from "@/admin/companies/companies-board-data"
import { loadFileRequests } from "@/admin/companies/file-requests-data"
import { CompaniesBoardFilter } from "@/constants/terminal/companies-board-filter"
import { CompanyDueReason } from "@/constants/terminal/company-due-reason"

const searchSchema = z.object({
  filter: z.nativeEnum(CompaniesBoardFilter).optional().catch(undefined),
  // Narrows a Due filter to one reason.
  dueReason: z.nativeEnum(CompanyDueReason).optional().catch(undefined),
  // Portfolio names. Not `portfolio`, which /terminal's search holds as one
  // slug: route search types merge in a `(prev) =>` updater.
  portfolios: z.array(z.string()).optional().catch(undefined),
  // Companies to arrive selected, as "Retry failed" on a run sends them.
  select: z.array(z.string().uuid()).optional().catch(undefined),
  // The Runs console lived here until the Companies board took the home.
  monitoringRunId: z.string().uuid().optional(),
})

const CompaniesBoardRoute = () => {
  const navigate = Route.useNavigate()
  const { filter, dueReason, portfolios, select = [] } = Route.useSearch()
  return (
    <CompaniesBoardPage
      // A new `select` starts a new selection.
      key={select.join()}
      {...Route.useLoaderData()}
      initialSelectedIds={select}
      filter={filter}
      onFilterChange={(next) =>
        void navigate({ search: { filter: next, portfolios, select } })
      }
      dueReason={dueReason}
      onDueReasonChange={(next) =>
        void navigate({
          search: { filter, dueReason: next, portfolios, select },
        })
      }
      portfolioNames={portfolios ?? []}
      onPortfolioNamesChange={(next) =>
        void navigate({
          search: {
            filter,
            dueReason,
            portfolios: next.length > 0 ? next : undefined,
            select,
          },
        })
      }
    />
  )
}

export const Route = createFileRoute("/_terminalAdmin/terminal/admin/")({
  validateSearch: searchSchema,
  beforeLoad: ({ search: { monitoringRunId } }) => {
    if (monitoringRunId)
      throw redirect({
        to: "/terminal/admin/runs",
        search: { monitoringRunId },
        replace: true,
      })
  },
  loader: async () => {
    const [rows, portfolios, fileRequests] = await Promise.all([
      loadCompaniesBoard(),
      loadTerminalPortfolios(),
      loadFileRequests(),
    ])
    return { rows, portfolios, fileRequests }
  },
  component: CompaniesBoardRoute,
})
